import { toast } from "sonner";
import {
  isRevokedSessionError,
  isSignInSurface,
  resolveSignInRouteForPath,
} from "@/lib/authSessionLifecycle";
import { diagnosticErrorDetails } from "@/lib/factoryAuthDiagnostics";
import { logPortalAuthDiagnostic } from "@/lib/portalAuthDiagnostics";

let lastSessionExpiredAt = 0;
const SESSION_EXPIRED_DEBOUNCE_MS = 3000;

export async function handleSessionExpired(
  signOutLocal: () => Promise<unknown>
) {
  if (typeof window === "undefined") return;
  // Already on a sign-in surface — nothing to redirect away from (loop guard).
  if (isSignInSurface(window.location.pathname)) return;

  const now = Date.now();
  if (now - lastSessionExpiredAt < SESSION_EXPIRED_DEBOUNCE_MS) return;
  lastSessionExpiredAt = now;

  const target = resolveSignInRouteForPath(window.location.pathname);

  toast.error("Your session has expired. Please sign in again.");
  try {
    await signOutLocal();
  } catch (e) {
    console.warn("Local sign-out after session expiry failed:", e);
  }
  window.location.replace(target);
}

/**
 * Entry point for ordinary PostgREST / RPC failures (React Query global error
 * handlers). Only a genuinely revoked or expired session triggers sign-out —
 * 403 / RLS denials with a valid session are ignored.
 */
type SessionRefreshResult = {
  data?: { session?: unknown | null };
  error?: { code?: string; message?: string } | null;
};

export type SessionRecoveryOutcome = 'ignored' | 'refreshed' | 'expired' | 'indeterminate';

let refreshInFlight: Promise<SessionRefreshResult> | null = null;

function isTerminalRefreshError(error: SessionRefreshResult['error']): boolean {
  const text = `${error?.code ?? ''} ${error?.message ?? ''}`.toLowerCase();
  return [
    'invalid_grant',
    'refresh_token_not_found',
    'refresh_token_already_used',
    'session_not_found',
    'session_missing',
    'auth session missing',
    'invalid refresh token',
    'refresh token not found',
  ].some((pattern) => text.includes(pattern));
}

/**
 * A request-level 401 does not prove the user's Supabase session is invalid.
 * Refresh first, and only redirect when Auth confirms the refresh token is
 * terminally invalid. A transient Auth/network error leaves the session intact.
 */
export async function maybeHandleRevokedSession(
  error: unknown,
  refreshSession: () => Promise<SessionRefreshResult>,
  signOutLocal: () => Promise<unknown>
): Promise<SessionRecoveryOutcome> {
  if (!isRevokedSessionError(error)) return 'ignored';

  if (!refreshInFlight) {
    refreshInFlight = Promise.resolve().then(refreshSession).finally(() => {
      refreshInFlight = null;
    });
  }

  let recovery: SessionRefreshResult;
  try {
    recovery = await refreshInFlight;
  } catch {
    void logPortalAuthDiagnostic('supabase_session_recovery', {
      reason: 'refresh_indeterminate',
      source: 'postgrest_or_rpc_recovery',
    });
    return 'indeterminate';
  }

  const refreshReason = recovery.data?.session
    ? 'refresh_succeeded'
    : recovery.error && isTerminalRefreshError(recovery.error)
      ? 'refresh_terminal_failure'
      : 'refresh_indeterminate';
  const refreshErrorCode = typeof recovery.error?.code === 'string' && /^[a-z0-9_-]{1,80}$/i.test(recovery.error.code)
    ? recovery.error.code
    : null;
  void logPortalAuthDiagnostic('supabase_session_recovery', {
    reason: refreshReason,
    source: 'postgrest_or_rpc_recovery',
    refresh_error_code: refreshErrorCode,
  });

  if (recovery.data?.session) return 'refreshed';
  if (recovery.error && !isTerminalRefreshError(recovery.error)) return 'indeterminate';

  const details = diagnosticErrorDetails(error);
  void logPortalAuthDiagnostic('automatic_session_expiry_redirect', {
    reason: 'session_expired_or_revoked',
    source: 'postgrest_or_rpc',
    status: typeof details.status === 'number' ? details.status : null,
    code: typeof details.code === 'string' ? details.code : null,
    refresh_error_code: recovery.error?.code ?? null,
  });
  await handleSessionExpired(signOutLocal);
  return 'expired';
}
